import React, { useState } from 'react';
import { FadeIn, CTAButton, Section, SectionTitle, Accent, pad2 } from './Shared';
import { SecurePaymentNote } from './SecurePaymentNote';
import { useCountdown } from '../hooks/useCountdown';
import { markCheckoutStarted } from '../hooks/useExitIntent';
import { PRODUCTS } from './MetaPixel';
import { volumes, sessionFeatures, featureChips } from '../data/volumes';
import { bonuses } from '../data/bonuses';
import { Check, AlertTriangle, Plus, PrinterIcon, ArrowRightIcon, Lock, ZapIcon, ShieldCheck, BookOpenIcon, GiftIcon } from 'lucide-react';
export const ProductTomos = () => {
  const [openVolume, setOpenVolume] = useState<number | null>(0);
  return <Section className="bg-slate-950" data-id="element-30" data-mp="2cpykdjm5u63c">
      <FadeIn data-id="element-31" data-mp="2cox0cpn6299h">
        <SectionTitle sub="Jeden tom na każde dwa miesiące. Otwierasz, wybierasz sesję, prowadzisz." data-id="element-32" data-mp="2cof43t0i7v1k">
          6 kompletnych <Accent data-id="element-33" data-mp="30dg6ozmnobyx">tomów</Accent>
        </SectionTitle>
      </FadeIn>

      {/* Year timeline */}
      <FadeIn delay={0.05} className="mx-auto mb-8 max-w-4xl" data-id="element-34" data-mp="357khxv2yf65t">
        <div className="grid grid-cols-6 gap-1.5 md:gap-2" aria-label="Twój rok, tom po tomie" data-id="element-35" data-mp="2hmmp02hi6yt7">
          {volumes.map(v => <div key={v.number} className="text-center md:text-left" data-id="element-36" data-mp="2co33222wtn0u">
              <div className={`h-1.5 rounded-full ${v.bar}`} data-id="element-37" data-mp="2rbo47qg85c64" />
              <p className={`mt-2 font-display text-[11px] font-extrabold uppercase md:text-sm ${v.text}`} data-id="element-38" data-mp="2rd7ods5047d6">
                Tom {v.number}
              </p>
              <p className="hidden text-xs text-slate-500 md:block" data-id="element-39" data-mp="30arnaufe5rkm">{v.months}</p>
            </div>)}
        </div>
      </FadeIn>

      <FadeIn delay={0.1} className="mb-12 md:mb-16" data-id="element-40" data-mp="2wck8i54hn0c2">
        <ul className="mx-auto flex max-w-3xl flex-wrap justify-center gap-1.5 md:gap-2" data-id="element-41" data-mp="3aaihev01i4k6">
          {featureChips.map(chip => <li key={chip} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-slate-200 md:text-sm" data-id="element-42" data-mp="35ch1gixwulcx">
              <Check size={12} className="text-sky-400" strokeWidth={3} aria-hidden="true" data-id="element-43" data-mp="1ayg27wyg7wcb" />
              {chip}
            </li>)}
        </ul>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400 md:text-sm" data-id="element-44" data-mp="3b1qnnq6bandi">
          <PrinterIcon size={14} aria-hidden="true" data-id="element-45" data-mp="3k0c4ngw4xuc6" />
          <span data-id="element-46" data-mp="3jyra3r9tmbeb"><span className="font-semibold text-slate-200" data-id="element-47" data-mp="3jyre02cxyvjl">Gotowe do druku</span> lub do używania prosto z telefonu czy tabletu.</span>
        </p>
      </FadeIn>

      <h3 className="mb-5 text-center text-xs font-semibold uppercase tracking-widest text-slate-400" data-id="element-48" data-mp="3tz1mj9o17esf">
        Co otrzymujesz
      </h3>
      <div className="mb-16 grid grid-cols-1 items-start gap-3 md:mb-24 md:grid-cols-2 md:gap-4 lg:grid-cols-3" data-id="element-49" data-mp="2q4ebf01m0je">
        {volumes.map((v, i) => {
        const isOpen = openVolume === i;
        return <FadeIn key={v.number} delay={Math.min(i * 0.04, 0.2)} data-id="element-50" data-mp="bjhcavcyrscc" data-mpi={i}>
              <button type="button" onClick={() => setOpenVolume(isOpen ? null : i)} className={`group w-full rounded-2xl border border-t-[3px] bg-white/[0.03] p-5 text-left transition-[border-color,background-color] duration-200 ease-out hover:bg-white/[0.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 md:p-6 ${v.border} ${isOpen ? 'border-x-white/20 border-b-white/20' : 'border-x-white/10 border-b-white/10'}`} aria-expanded={isOpen} data-id="element-51" data-mp="gkeuh7xz4mdu">
                <div className="flex items-start justify-between gap-3" data-id="element-52" data-mp="hof6urupmwcs">
                  <div className="min-w-0" data-id="element-53" data-mp="1ebtmimsjip9r">
                    <p className={`font-display text-xs font-extrabold uppercase tracking-wide ${v.text}`} data-id="element-54" data-mp="1eepkuc8avfyq">
                      Tom {v.number} · {v.months}
                    </p>
                    <h4 className="mt-1.5 font-display text-lg font-extrabold leading-tight text-white md:text-xl" data-id="element-55" data-mp="14l8s7bjwg6wn">
                      {v.title}
                    </h4>
                    <p className="mt-1 text-sm text-slate-400" data-id="element-56" data-mp="1x4e8dk9tnu42">{v.tagline}</p>
                  </div>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-white transition-transform duration-200 ease-out ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" data-id="element-57" data-mp="1sbultxt5pon4">
                    <Plus size={16} data-id="element-58" data-mp="1hvaj4j7wofhb" />
                  </span>
                </div>
                <div className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-59" data-mp="13v9kzbj0zm5j">
                  <div className="overflow-hidden" data-id="element-60" data-mp="1vkm8zx5d67ff">
                    <p className="pt-4 text-[15px] leading-relaxed text-slate-300" data-id="element-61" data-mp="1vn7agl1r2lcn">
                      {v.desc}
                    </p>
                    <p className="mt-3 inline-flex rounded-md bg-white/5 px-2 py-0.5 text-xs font-semibold text-slate-200" data-id="element-62" data-mp="1mtc459ow6ovf">
                      {v.sessions}
                    </p>
                  </div>
                </div>
              </button>
            </FadeIn>;
      })}
      </div>

      <FadeIn data-id="element-63" data-mp="2jaghy3mkbbue">
        <div className="grid gap-8 rounded-3xl border border-white/10 bg-slate-900/60 p-6 md:grid-cols-[0.9fr_1.1fr] md:gap-12 md:p-12" data-id="element-64" data-mp="2jb8af5kv4tgg">
          <div className="flex flex-col" data-id="element-65" data-mp="7u52h3vs4rfd">
            <h3 className="text-center font-display text-3xl font-black uppercase italic leading-[0.95] tracking-tight text-white md:text-left md:text-5xl" data-id="element-66" data-mp="2wwkp4ejmpen">
              Każda sesja <Accent data-id="element-67" data-mp="hnly8dfh8glo">zawiera</Accent>
            </h3>
            
          </div>
          <ul className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2" data-id="element-68" data-mp="cmy9jyeq0bum">
            {sessionFeatures.map(feature => <li key={feature.title} className="flex items-start gap-3" data-id="element-69" data-mp="3l2hyil8cz83v">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15" data-id="element-70" data-mp="3g4gh04qkzgui">
                  <Check size={12} className="text-emerald-400" strokeWidth={3} aria-hidden="true" data-id="element-71" data-mp="3v1qqx9nkseo4" />
                </span>
                <div data-id="element-72" data-mp="1aa6pzezv6upr">
                  <p className="font-semibold leading-snug text-white" data-id="element-73" data-mp="1a9ovonwriqkh">{feature.title}</p>
                  <p className="text-sm leading-normal text-slate-400" data-id="element-74" data-mp="15bdlgc2k2e1q">{feature.desc}</p>
                </div>
              </li>)}
          </ul>
        <p className="text-center text-lg font-semibold leading-snug text-sky-300 md:col-span-2 md:text-xl" data-id="element-75" data-mp="1cdiio3iziren">
              Otwierasz tom, wybierasz sesję i prowadzisz. To takie proste. 365
              zajęć na wysokim poziomie. Gotowe już dziś.
            </p></div>
      </FadeIn>
    </Section>;
};
export const Bonuses = () => {
  return <Section className="bg-slate-900/40" data-id="element-76" data-mp="1r989cez47spp">
      <FadeIn data-id="element-77" data-mp="tolos0ri20wy">
        <SectionTitle sub="Ekskluzywne materiały — bez żadnych dopłat" data-id="element-78" data-mp="tp54datogd7t">
          + 5 strategicznych <Accent data-id="element-79" data-mp="ynyjt8siyeam">bonusów</Accent>
        </SectionTitle>
      </FadeIn>

      <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 md:mb-10 md:gap-4 lg:grid-cols-5" data-id="element-80" data-mp="13m9rbcvckcpo">
        {bonuses.map((bono, i) => <FadeIn key={bono.num} delay={Math.min(i * 0.04, 0.2)} className={i === bonuses.length - 1 ? 'sm:col-span-2 lg:col-span-1' : ''} data-id="element-81" data-mp="18drtg493wjc8" data-mpi={i}>
            <article className="flex h-full gap-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-3 sm:flex-col sm:gap-0 sm:p-0" data-id="element-82" data-mp="av8890oj9x0v">
              <img src={bono.image} alt={`Okładka bonusu ${bono.num}: ${bono.title}`} loading="lazy" decoding="async" width={1024} height={1024} className="aspect-square w-28 shrink-0 self-start rounded-xl object-cover sm:w-full sm:rounded-none" data-id="element-83" data-mp="2t5np25suwwrf" />
              <div className="flex min-w-0 flex-1 flex-col sm:p-4" data-id="element-84" data-mp="2k7tvv3qe2j8h">
                <p className="font-display text-xs font-extrabold uppercase tracking-wide text-sky-400" data-id="element-85" data-mp="2f6ea19ge08p1">
                  Bonus {bono.num}
                </p>
                <h3 className="mt-1 font-display text-base font-extrabold leading-tight text-white" data-id="element-86" data-mp="25gkz8ao2jxlu">
                  {bono.title}
                </h3>
                <p className="mt-1.5 text-sm leading-snug text-slate-400" data-id="element-87" data-mp="21fr9rcuzaaqu">
                  {bono.desc}
                </p>
                <div className="mt-auto flex items-center justify-between gap-2 pt-3" data-id="element-88" data-mp="1rhbty61einwn">
                  <span className="text-sm text-slate-500 line-through" data-id="element-89" data-mp="1rj654smvf1nj">
                    {bono.value}
                  </span>
                  <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-emerald-400" data-id="element-90" data-mp="3bbwisxdyq0pt">
                    Gratis
                  </span>
                </div>
              </div>
            </article>
          </FadeIn>)}
      </div>

      <FadeIn className="mx-auto flex max-w-3xl flex-col items-center justify-between gap-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] px-6 py-5 text-center md:flex-row md:text-left" data-id="element-91" data-mp="36dcx8y3a6qxz">
        <div className="flex items-center gap-3" data-id="element-92" data-mp="2wex3encvd3ks">
          <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 md:flex" data-id="element-93" data-mp="2wgq9o1jklls9">
            <GiftIcon size={22} className="text-emerald-400" aria-hidden="true" data-id="element-94" data-mp="2ilnz313s3894" />
          </span>
          <div data-id="element-95" data-mp="2nkr4imno7ghm">
            <p className="text-sm text-slate-300" data-id="element-96" data-mp="28pku7nhjkvih">Łączna wartość 5 bonusów: <span className="text-slate-500 line-through" data-id="element-97" data-mp="28q4br33a5hvz">195 zł</span></p>
            <p className="text-xs text-slate-500" data-id="element-98" data-mp="2dnmeucum92w2">W pakiecie Trening Funkcjonalny 365 — bez dopłat.</p>
          </div>
        </div>
        <p className="font-display text-3xl font-black uppercase italic leading-none tracking-tight text-emerald-400 md:text-4xl" data-id="element-99" data-mp="1u35ozlfdbbnl">
          Dziś gratis
        </p>
      </FadeIn>
    </Section>;
};
export const UrgencyCounter = () => {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown();
  const units = [{
    value: hours,
    label: 'godz'
  }, {
    value: minutes,
    label: 'min'
  }, {
    value: seconds,
    label: 'sek'
  }];
  return <Section className="bg-slate-950 !py-14 md:!py-20" data-id="element-100" data-mp="290y1s5tj5ly3">
      <FadeIn className="mx-auto max-w-xl text-center" data-id="element-101" data-mp="28ybvsamafehz">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-400" data-id="element-102" data-mp="2mq88p7fdxhts">
          Cena premierowa — koniec za
        </p>
        <div className="mt-4 flex items-start justify-center gap-2 md:gap-3" role="timer" aria-live="off" data-id="element-103" data-mp="2hujfrmxvez6g">
          {units.map((unit, i) => <React.Fragment key={unit.label} data-id="element-104" data-mp="2rqw6qd216yxi" data-mpi={i}>
              {i > 0 && <span className="pt-3 font-display text-4xl font-black text-slate-600 md:pt-4 md:text-5xl" aria-hidden="true" data-id="element-105" data-mp="2rsh40afqcvz2">:</span>}
              <div className="flex w-20 flex-col items-center rounded-2xl border border-white/10 bg-white/[0.04] py-3 md:w-24 md:py-4" data-id="element-106" data-mp="2m72d4y7ozkus">
                <span className="font-display text-4xl font-black tabular-nums leading-none tracking-tight text-white md:text-5xl" data-id="element-107" data-mp="2h8r06em3ou8c">
                  {pad2(unit.value)}
                </span>
                <span className="mt-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-500" data-id="element-108" data-mp="39vbthkm4zgej">
                  {unit.label}
                </span>
              </div>
            </React.Fragment>)}
        </div>

        <p className="mt-6 flex items-center justify-center gap-2 text-base font-semibold text-amber-300 md:text-lg" data-id="element-109" data-mp="30u5an4w0lti4">
          <AlertTriangle size={18} aria-hidden="true" data-id="element-110" data-mp="2vypychdno5nt" />
          Potem cena rośnie. Na stałe.
        </p>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left" data-id="element-111" data-mp="1pe6xaott0lb3">
          <div className="mb-3 flex items-center justify-between gap-3 text-sm text-slate-300" data-id="element-112" data-mp="1kjt8j59ndcfe">
            <span className="flex items-center gap-2" data-id="element-113" data-mp="3w2z3rj8i0thi">
              <span className="h-2 w-2 rounded-full bg-sky-400 animate-pulse" aria-hidden="true" data-id="element-114" data-mp="4zk4xnmdd3kf" />
              Miejsca w cenie premierowej
            </span>
            <span className="shrink-0 font-semibold text-sky-400" data-id="element-115" data-mp="dqsy8jtkhsc1">Zostało niewiele</span>
          </div>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={71} aria-valuemin={0} aria-valuemax={100} aria-label="Zajęte miejsca" data-id="element-116" data-mp="iq5s3oiuvzo4">
            <div className="h-full rounded-full bg-blue-500" style={{
            width: '71%'
          }} data-id="element-117" data-mp="so3es7l2hoem" />
          </div>
          <p className="text-right text-xs text-slate-500" data-id="element-118" data-mp="wpyg3c3001se">
            71% miejsc już zajętych
          </p>
        </div>
      </FadeIn>
    </Section>;
};
export const OfferCard = ({
  id,
  footnote
}: {
  id?: string;
  footnote: string;
}) => {
  const includes = [{
    icon: BookOpenIcon,
    label: '6 kompletnych tomów',
    detail: '365 sesji gotowych do poprowadzenia',
    price: '269 zł'
  }, {
    icon: GiftIcon,
    label: '5 ekskluzywnych bonusów',
    detail: 'Modyfikacje, rozgrzewki, wyciszenia i scenariusze',
    price: '195 zł'
  }];
  return <div id={id} className="relative rounded-3xl border border-sky-400/40 bg-slate-900 p-5 shadow-2xl shadow-blue-950/50 md:p-8" data-id="element-119" data-mp="ekwqex1p7t74">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-blue-600 px-3.5 py-1 text-[11px] font-bold uppercase tracking-widest text-white" data-id="element-120" data-mp="okvyyty4bjjz">
        Najczęściej wybierany
      </div>

      <p className="mt-2 text-center font-display text-xl font-black uppercase italic tracking-tight text-white md:text-2xl" data-id="element-121" data-mp="ya76ic5cwg5r">
        Trening Funkcjonalny <Accent data-id="element-122" data-mp="tei3tzy1ij58">365™</Accent>
      </p>

      <ul className="mt-5 divide-y divide-white/10 border-y border-white/10" data-id="element-123" data-mp="17b5e14f075t3">
        {includes.map(({
        icon: Icon,
        label,
        detail,
        price
      }) => <li key={label} className="flex items-center justify-between gap-3 py-3" data-id="element-124" data-mp="1lym3o1k32oid">
            <div className="flex min-w-0 items-start gap-3" data-id="element-125" data-mp="1lwsyyrt16ed0">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5" data-id="element-126" data-mp="1qwdu0r52y2kg">
                <Icon size={16} className="text-sky-400" aria-hidden="true" data-id="element-127" data-mp="1cxe7bp14nxqx" />
              </span>
              <div className="min-w-0" data-id="element-128" data-mp="1gxy6ey59hbfp">
                <p className="text-sm font-semibold leading-snug text-white md:text-base" data-id="element-129" data-mp="1384h6epugp5g">{label}</p>
                <p className="text-xs leading-snug text-slate-400 md:text-sm" data-id="element-130" data-mp="17yuo3wdx5029">{detail}</p>
              </div>
            </div>
            <span className="shrink-0 text-sm text-slate-500 line-through" data-id="element-131" data-mp="taq14g7u22do">{price}</span>
          </li>)}
      </ul>

      <div className="mt-6 text-center" data-id="element-132" data-mp="y9t6k1rq6am6">
        <p className="text-sm text-slate-400" data-id="element-133" data-mp="y6ou6h43w8kf">
          Wartość regularna <span className="text-slate-500 line-through" data-id="element-134" data-mp="k9s62nfdh578">464 zł</span>
          <span className="block" data-id="element-135" data-mp="p8mmjipvaho6">Pełny dostęp już dziś za</span>
        </p>
        <p className="mt-2 font-display font-black italic leading-none tracking-tight text-white" data-id="element-136" data-mp="h7nm4dab2wzs">
          <span className="text-7xl md:text-8xl" data-id="element-137" data-mp="c8kld51czcxm">79</span>
          <span className="ml-2 text-3xl md:text-4xl" data-id="element-138" data-mp="c940yf3jdp8h">zł</span>
        </p>
        <p className="mt-2 text-xs text-slate-400 md:text-sm" data-id="element-139" data-mp="7gsq4h79cj15">{footnote}</p>
      </div>

      <div className="mt-6" data-id="element-140" data-mp="3h238okuqn1w">
        <CTAButton href={PRODUCTS.tf365.checkoutUrl} fullWidth trackingProduct="tf365" onClick={markCheckoutStarted} data-id="element-141" data-mp="3fqzgorepfas">
          Odbierz pełny pakiet
          <ArrowRightIcon size={18} className="transition-transform duration-200 group-hover/cta:translate-x-0.5" aria-hidden="true" data-id="element-142" data-mp="3pnql7uncrdci" />
        </CTAButton>
        <SecurePaymentNote className="mt-4" data-id="element-143" data-mp="3kow18pdnfiqt" />
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-500" data-id="element-144" data-mp="3gn276zxylczm">
          <span className="flex items-center gap-1" data-id="element-145" data-mp="1ixancjioipou"><Lock size={12} aria-hidden="true" data-id="element-146" data-mp="1iw7wucp9qp9g" /> Bezpieczna płatność</span>
          <span className="flex items-center gap-1" data-id="element-147" data-mp="1ewh0ltgz3gxj"><ZapIcon size={12} aria-hidden="true" data-id="element-148" data-mp="1etbd2inzsn3x" /> Dostęp w kilka minut</span>
          <span className="flex items-center gap-1" data-id="element-149" data-mp="2wsixy59g4fwy"><ShieldCheck size={12} aria-hidden="true" data-id="element-150" data-mp="31rvmqvjvnx26" /> 7 dni gwarancji</span>
        </div>
      </div>
    </div>;
};
export const Pricing = () => {
  return <Section className="bg-slate-900/40" id="pricing" data-id="element-151" data-mp="36jdohltq61o7">
      <div className="mx-auto max-w-lg" data-id="element-152" data-mp="3ak78w57ykycc">
        <FadeIn className="mb-10 text-center" data-id="element-153" data-mp="3ajxk2kyngx88">
          <p className="text-sm text-slate-400 md:text-base" data-id="element-154" data-mp="2dug5thllq2ux">
            Dzieli Cię jeden krok od tego, by mieć wszystkie zajęcia z głowy…
          </p>
          <h2 className="mx-auto mt-3 font-display text-4xl font-black uppercase italic leading-[0.92] tracking-tight text-white md:text-6xl" data-id="element-155" data-mp="2itt20syd4ma6">
            Specjalna oferta <Accent data-id="element-156" data-mp="2ruh891hilici">premierowa</Accent>
          </h2>
          <p className="mt-3 text-sm text-slate-400" data-id="element-157" data-mp="2rruz4xez6us6">
            Tomy, bonusy i 365 gotowych sesji w jednym dostępie.
          </p>
        </FadeIn>

        <FadeIn delay={0.05} data-id="element-158" data-mp="72xje572voui">
          <OfferCard footnote="Jednorazowa płatność · Bez subskrypcji · Natychmiastowy dostęp" data-id="element-159" data-mp="725net9kjp3p" />
        </FadeIn>
      </div>
    </Section>;
};